// src/components/StateLookupDialog.tsx
import React, { useState } from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { stateLegislatorUrls } from '../utils/stateLegislatorUrls';
import { COUNTRIES } from '../utils/stateUtils';

interface StateLookupDialogProps {
  open: boolean;
  onClose: () => void;
}

const StateLookupDialog: React.FC<StateLookupDialogProps> = ({ open, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<string>('united-states');

  // Filter states based on search term (case-insensitive)
  const filteredStates = stateLegislatorUrls.filter((item) =>
    item.state.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleStateClick = (url: string) => {
    if (!url) {
      console.error('State URL is missing');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="state-lookup-dialog-title">
      <DialogTitle id="state-lookup-dialog-title">
        Find Your Legislature
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="country-filter-label">Country</InputLabel>
          <Select
            labelId="country-filter-label"
            value={selectedCountry}
            label="Country"
            onChange={(e) => {
              setSelectedCountry(e.target.value as string);
              setSearchTerm('');
            }}
          >
            {COUNTRIES.map((c) => (
              <MenuItem key={c.slug} value={c.slug}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedCountry !== 'united-states' ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Legislator lookup is only available for U.S. states. For Canadian legislative information, please visit your
            provincial or territorial legislature's website directly.
          </Alert>
        ) : (
          <>
            <TextField
              autoFocus
              fullWidth
              label="Search states"
              placeholder="Search states..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {filteredStates.length > 0 ? (
                <List>
                  {filteredStates.map((item) => (
                    <ListItemButton key={item.state} onClick={() => handleStateClick(item.stateUrlFindLeg)}>
                      <ListItemText primary={item.state} />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No states match your search
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StateLookupDialog;
